import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Smile, X } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchStickerSets, moderateStickerSet, type AdminStickerSetRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'

/**
 * A21.01 — Stiker to'plamlari moderatsiyasi (10 §A21). GET /admin/stickers/sets;
 * tasdiq/rad → POST /admin/stickers/sets/:id/moderate {approve, reason}. `active`
 * to'plam do'konda ko'rinadi va yuborilishi mumkin; `rejected` yashiriladi.
 */
export function StickerModeration() {
  const t = useT()
  const { toast } = useToast()
  const [rejectTarget, setRejectTarget] = useState<AdminStickerSetRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.stickers.done, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(t.stickers.failed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
        throw e
      }
    },
    [toast, t],
  )

  const statusKind = (s: string): StatusKind =>
    s === 'active' ? 'active' : s === 'rejected' ? 'deleted' : 'pending'
  const statusLabel = (s: string) =>
    s === 'active' ? t.stickers.active : s === 'rejected' ? t.stickers.rejected : t.stickers.pending

  const columns = useMemo<AdminColumn<AdminStickerSetRow>[]>(
    () => [
      {
        key: 'title',
        header: t.stickers.title,
        type: 'text',
        minWidth: 220,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title || '—'}</span>
            <span className="block truncate text-xs text-secondary">
              {r.short_name ? `/${r.short_name}` : ''}
              {r.owner_name ? ` · ${r.owner_name}` : ` · #${r.owner_id}`}
            </span>
          </div>
        ),
      },
      {
        key: 'status',
        header: t.stickers.status,
        type: 'enum',
        width: 140,
        filterable: true,
        options: [
          { value: 'pending', label: t.stickers.pending, status: 'pending' },
          { value: 'active', label: t.stickers.active, status: 'active' },
          { value: 'rejected', label: t.stickers.rejected, status: 'deleted' },
        ],
        render: (r) => <StatusBadge status={statusKind(r.status)} label={statusLabel(r.status)} />,
      },
      { key: 'sticker_count', header: t.stickers.count, type: 'number', width: 100, align: 'right' },
      { key: 'installs', header: t.stickers.installs, type: 'number', width: 110, align: 'right' },
      {
        key: 'reject_reason',
        header: t.stickers.reason,
        type: 'text',
        minWidth: 160,
        render: (r) => <span className="text-secondary">{r.reject_reason || '—'}</span>,
      },
    ],
    [t],
  )

  const rowActions = useMemo<RowAction<AdminStickerSetRow>[]>(
    () => [
      {
        id: 'approve',
        label: t.stickers.approve,
        icon: <Check size={13} />,
        visible: (r) => r.status !== 'active',
        onSelect: (r) => void run(() => moderateStickerSet(r.id, true, '')),
      },
      {
        id: 'reject',
        label: t.stickers.reject,
        icon: <X size={13} />,
        danger: true,
        visible: (r) => r.status !== 'rejected',
        onSelect: (r) => setRejectTarget(r),
      },
    ],
    [t, run],
  )

  return (
    <>
      <PageHeader
        title={t.stickers.moderation}
        screenId="A21.01"
        actions={
          <Link to="/points/suspicions" className="text-sm text-accent hover:underline">
            {t.stickers.economyHint}
          </Link>
        }
      />
      <DataTable<AdminStickerSetRow>
        key={reloadKey}
        screenId="A21.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchStickerSets}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Smile size={22} />, title: t.stickers.empty }}
      />

      {rejectTarget && (
        <DangerDialog
          open
          onClose={() => setRejectTarget(null)}
          screenId="A21.01"
          level={2}
          title={`${t.stickers.reject} — ${rejectTarget.title}`}
          description={t.stickers.rejectPrompt}
          confirmLabel={t.stickers.reject}
          onConfirm={async ({ reason }) => {
            await run(() => moderateStickerSet(rejectTarget.id, false, reason))
            setRejectTarget(null)
          }}
        />
      )}
    </>
  )
}
